/**
 * Main Layout Component
 *
 * Primary application layout with sidebar navigation.
 * Mobile-first responsive design with bottom navigation.
 */

import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  FileText,
  Package,
  Users,
  Calculator,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ShoppingCart,
  ClipboardList,
  Truck,
  FolderTree,
  Tags,
  MapPin,
  UserCog,
  ChevronLeft,
  ChevronRight,
  Bell,
  BadgeCheck,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import api from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { useTenant } from '@/hooks/useTenant'
import { useTranslation } from '@/hooks/useTranslation'
import { Button } from '@/components/ui/button'
import { Logo } from '@/components/ui/Logo'
import { ThemeToggle } from '@/components/ThemeToggle'
import { UserAvatar, AvatarSelector } from '@/components/AvatarSelector'
import { cn } from '@/lib/utils'
import { ProductFooter } from '@/components/ProductFooter'
import { useApprovalPendingCount } from '@/hooks/useApprovals'
import { useToast } from '@/components/ui/toaster'

// Navigation item type
type MenuRole = 'admin' | 'seller' | 'accountant'

interface NavItem {
  path: string
  icon: React.ComponentType<{ className?: string }>
  labelKey: string
  /** Quiénes lo ven. Sin la lista, todos. */
  roles?: MenuRole[]
}

const SALES: MenuRole[] = ['admin', 'seller']
const ADMIN_ONLY: MenuRole[] = ['admin']

// Los roles viejos (gerente, cajero) trabajan como vendedor.
function menuRole(role: string | undefined): MenuRole {
  return role === 'admin' || role === 'accountant' ? role : 'seller'
}

function useVisibleItems(items: NavItem[]) {
  const { user } = useAuth()
  const role = menuRole(user?.role)
  return items.filter((item) => !item.roles || item.roles.includes(role))
}

// Full navigation items for sidebar
const navItems: NavItem[] = [
  { path: '/', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
  { path: '/bills', icon: FileText, labelKey: 'nav.bills' },
  { path: '/proformas', icon: ClipboardList, labelKey: 'nav.proformas', roles: SALES },
  { path: '/purchases', icon: ShoppingCart, labelKey: 'nav.purchases' },
  { path: '/articles', icon: Package, labelKey: 'nav.articles' },
  { path: '/clients', icon: Users, labelKey: 'nav.clients' },
  { path: '/providers', icon: Truck, labelKey: 'nav.providers' },
  { path: '/cashbox', icon: Calculator, labelKey: 'nav.cashbox' },
  { path: '/reports', icon: BarChart3, labelKey: 'nav.reports' },
  { path: '/families', icon: FolderTree, labelKey: 'nav.families', roles: SALES },
  { path: '/groups', icon: Tags, labelKey: 'nav.groups', roles: SALES },
  { path: '/locations', icon: MapPin, labelKey: 'nav.locations', roles: SALES },
  { path: '/approvals', icon: BadgeCheck, labelKey: 'nav.approvals', roles: SALES },
  { path: '/users', icon: UserCog, labelKey: 'nav.users', roles: ADMIN_ONLY },
  { path: '/settings', icon: Settings, labelKey: 'nav.settings' },
]

// Bottom navigation items (5 most used)
const bottomNavItems: NavItem[] = [
  { path: '/', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
  { path: '/bills', icon: FileText, labelKey: 'nav.bills' },
  { path: '/articles', icon: Package, labelKey: 'nav.articles' },
  { path: '/clients', icon: Users, labelKey: 'nav.clients' },
  { path: '/cashbox', icon: Calculator, labelKey: 'nav.cashbox' },
]

/**
 * Bottom Navigation Component (Mobile)
 */
function BottomNavigation() {
  const { t } = useTranslation()
  const location = useLocation()
  const items = useVisibleItems(bottomNavItems)

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  // El POS tiene su propia barra fija de cobro en el mismo lugar
  if (location.pathname === '/bills/new') return null

  return (
    <nav aria-label={t('nav.mainMenu')} className="fixed bottom-0 left-0 right-0 bg-card border-t z-40 md:hidden safe-area-bottom">
      <div className="flex justify-around items-center h-16">
        {items.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={cn(
              'flex flex-col items-center justify-center flex-1 h-full px-2 transition-colors touch-manipulation',
              isActive(item.path)
                ? 'text-primary'
                : 'text-muted-foreground active:text-primary'
            )}
          >
            <item.icon aria-hidden="true" className={cn(
              'h-5 w-5 mb-1 transition-transform',
              isActive(item.path) && 'scale-110'
            )} />
            <span className="text-[11px] font-medium truncate">
              {t(item.labelKey)}
            </span>
          </NavLink>
        ))}
      </div>
    </nav>
  )
}

// El botón flotante va solo en las listas: en un detalle o un formulario tapa
// acciones del pie de la página (imprimir, ver factura) y no aporta nada.
const FAB_ROUTES = ['/bills', '/articles', '/clients']

/**
 * Floating Action Button (Mobile) - Premium Design
 *
 * Large, prominent button for quick access to create new invoices.
 * Context-aware: changes action based on current page.
 */
function FloatingActionButton() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { isAccountant, isAdmin } = useAuth()

  if (isAccountant || !FAB_ROUTES.includes(location.pathname)) return null
  // Crear artículos es solo del admin.
  if (!isAdmin && location.pathname.startsWith('/articles')) return null

  // Determine action based on current page
  const getAction = () => {
    if (location.pathname.startsWith('/articles')) {
      return { path: '/articles/new', label: t('articles.newArticle'), icon: Package }
    }
    if (location.pathname.startsWith('/clients')) {
      return { path: '/clients/new', label: t('clients.newClient'), icon: Users }
    }
    // Default: New Bill (most common action)
    return { path: '/bills/new', label: t('bills.newBill'), icon: ShoppingCart }
  }

  const action = getAction()
  const Icon = action.icon

  return (
    <Button
      className={cn(
        "fixed bottom-20 right-4 z-30 md:hidden",
        "h-14 px-5 rounded-full",
        "shadow-xl hover:shadow-2xl",
        "bg-primary hover:bg-primary/90",
        "transition-shadow duration-300",
        "animate-in slide-in-from-bottom-4 fade-in",
        "active:scale-95"
      )}
      onClick={() => navigate(action.path)}
    >
      <Icon className="h-5 w-5 mr-2" aria-hidden="true" />
      <span className="font-medium">{action.label}</span>
    </Button>
  )
}

/**
 * Sidebar Navigation Component
 */
function Sidebar({
  isOpen,
  onClose,
  isCollapsed,
  canToggle,
  onToggleCollapse,
}: {
  isOpen: boolean
  onClose: () => void
  isCollapsed: boolean
  canToggle: boolean
  onToggleCollapse: () => void
}) {
  const { t } = useTranslation()
  const items = useVisibleItems(navItems)
  // Menú móvil: Escape lo cierra y el foco entra al abrirlo
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!isOpen) return
    closeButtonRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        aria-label={t('nav.mainMenu')}
        className={cn(
          'fixed top-0 left-0 z-50 h-[100dvh] flex flex-col bg-card border-r transform transition-[transform,width] duration-200 ease-in-out md:translate-x-0 md:static md:h-full',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          isCollapsed ? 'md:w-16' : 'md:w-64',
          'w-64' // Mobile always full width
        )}
      >
        {/* Header */}
        <div className={cn(
          "flex items-center h-16 border-b shrink-0",
          isCollapsed ? "flex-col justify-center gap-1 py-2 px-1" : "justify-between px-4"
        )}>
          <NavLink
            to="/"
            aria-label={t('nav.dashboard')}
            className="flex items-center hover:opacity-90 transition-opacity"
            onClick={onClose}
          >
            <Logo
              size={isCollapsed ? 'sm' : 'md'}
              showText={!isCollapsed}
              variant="default"
            />
          </NavLink>
          {/* Collapse toggle - desktop only (en tablet el menú va siempre colapsado) */}
          {canToggle && (
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "hidden md:flex",
                isCollapsed ? "h-6 w-6" : "h-8 w-8"
              )}
              onClick={onToggleCollapse}
              aria-label={isCollapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
              title={isCollapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
            >
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          )}
          {/* Close button - mobile only */}
          <Button
            ref={closeButtonRef}
            variant="ghost"
            size="icon"
            className="md:hidden h-11 w-11"
            onClick={onClose}
            aria-label={t('nav.closeMenu')}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className={cn(
          "flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto overscroll-contain",
          isCollapsed ? "p-2" : "p-4"
        )}>
          {items.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              onClick={onClose}
              title={isCollapsed ? t(item.labelKey) : undefined}
              aria-label={isCollapsed ? t(item.labelKey) : undefined}
              className={({ isActive }) =>
                cn(
                  'flex items-center rounded-md text-sm font-medium transition-colors touch-manipulation',
                  isCollapsed
                    ? 'justify-center px-2 py-3'
                    : 'gap-3 px-3 py-3',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground active:bg-accent'
                )
              }
            >
              <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {!isCollapsed && <span className="truncate">{t(item.labelKey)}</span>}
            </NavLink>
          ))}
        </nav>

      </aside>
    </>
  )
}

/**
 * Campana de solicitudes: al admin le cuenta lo que espera su decisión; al
 * vendedor, las respuestas a sus solicitudes que todavía no vio. Solo aparece
 * cuando hay algo nuevo (la pantalla siempre está en el menú), y si el número
 * sube avisa con un mensaje.
 */
function ApprovalsBell() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { isAccountant, isAdmin, isAuthenticated } = useAuth()
  const { data: count = 0, isSuccess } = useApprovalPendingCount(isAuthenticated && !isAccountant)
  const lastCount = useRef<number | null>(null)
  useEffect(() => {
    if (!isSuccess) return
    // La primera lectura no avisa: es lo que ya había al entrar.
    if (lastCount.current !== null && count > lastCount.current) {
      toast({ title: t(isAdmin ? 'approvals.newRequestsToast' : 'approvals.newAnswersToast') })
    }
    lastCount.current = count
  }, [count, isSuccess, isAdmin, t, toast])
  if (isAccountant || count === 0) return null
  const label = t('approvals.bellPending', { count })
  return (
    <NavLink
      to="/approvals"
      aria-label={label}
      title={label}
      className="relative inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-accent"
    >
      <Bell className="h-5 w-5" aria-hidden="true" />
      <span className="absolute top-1 right-1 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold leading-[1.1rem] text-center">
        {count > 99 ? '99+' : count}
      </span>
    </NavLink>
  )
}

/**
 * Usuario en el header: avatar (cambia el avatar), nombre y rol, y cerrar sesión.
 * Antes vivía al pie del menú lateral: en el celular había que abrir el menú y
 * bajar hasta el final, y en pantallas bajas tapaba los últimos ítems.
 */
function HeaderUser() {
  const { t } = useTranslation()
  const { user, logout, refreshUser } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [avatarSelectorOpen, setAvatarSelectorOpen] = useState(false)

  const updateAvatarMutation = useMutation({
    mutationFn: async (avatarId: string) => {
      const response = await api.put('/users/me', { avatar: avatarId })
      return response.data
    },
    onSuccess: () => {
      refreshUser()
      queryClient.invalidateQueries({ queryKey: ['current-user'] })
      setAvatarSelectorOpen(false)
    },
  })

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const roleLabel = user?.role ? t(`users.${user.role}`) : ''

  return (
    <div className="flex items-center gap-2">
      <UserAvatar
        avatarId={user?.avatar}
        name={user?.full_name || t('users.user')}
        size="md"
        onClick={() => setAvatarSelectorOpen(true)}
      />
      <div className="hidden lg:block min-w-0 max-w-[12rem] leading-tight">
        <p className="text-sm font-medium truncate">{user?.full_name}</p>
        <p className="text-xs text-muted-foreground truncate">{roleLabel}</p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-10 w-10"
        onClick={handleLogout}
        aria-label={t('nav.logout')}
        title={t('nav.logout')}
      >
        <LogOut className="h-5 w-5" aria-hidden="true" />
      </Button>
      <AvatarSelector
        open={avatarSelectorOpen}
        onOpenChange={setAvatarSelectorOpen}
        currentAvatarId={user?.avatar}
        onSelect={(avatarId) => updateAvatarMutation.mutate(avatarId)}
        isLoading={updateAvatarMutation.isPending}
      />
    </div>
  )
}

/**
 * Main Layout Component
 */
// Tablet, también en horizontal: el menú lateral expandido (256px) dejaba las
// tablas y el POS sin espacio. Por ancho cubre las de hasta 1279 px; por
// pantalla táctil, el iPad Pro de 12,9" (1366 px).
const TABLET_QUERY =
  '(min-width: 768px) and (max-width: 1279px), (min-width: 768px) and (max-width: 1400px) and (pointer: coarse)'

function useIsTablet() {
  const [isTablet, setIsTablet] = useState(
    typeof window !== 'undefined' && window.matchMedia(TABLET_QUERY).matches
  )
  useEffect(() => {
    const mql = window.matchMedia(TABLET_QUERY)
    const onChange = (e: MediaQueryListEvent) => setIsTablet(e.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])
  return isTablet
}

export function MainLayout() {
  const { t } = useTranslation()
  const location = useLocation()
  const isTablet = useIsTablet()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('sidebarCollapsed') === 'true'
  })
  const { tenant, displayName } = useTenant()

  const handleToggleCollapse = () => {
    const newValue = !sidebarCollapsed
    setSidebarCollapsed(newValue)
    localStorage.setItem('sidebarCollapsed', String(newValue))
  }

  const hasFab = FAB_ROUTES.includes(location.pathname)
  const isPos = location.pathname === '/bills/new'

  // El scroll vive en <main>, no en la ventana, así que el navegador no lo
  // reinicia al cambiar de pantalla: la nueva abría a la altura de la anterior.
  // Solo con la ruta: cambiar filtros o página en la misma pantalla no salta.
  const mainRef = useRef<HTMLElement>(null)
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 })
  }, [location.pathname])

  return (
    <div className="flex h-[100dvh] bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[60] focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:text-sm focus:shadow"
      >
        {t('nav.skipToContent')}
      </a>

      {/* Sidebar - hidden on mobile */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed || isTablet}
        canToggle={!isTablet}
        onToggleCollapse={handleToggleCollapse}
      />

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 md:h-16 border-b bg-card flex items-center px-4 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden mr-2 h-10 w-10"
            onClick={() => setSidebarOpen(true)}
            aria-label={t('nav.openMenu')}
            aria-expanded={sidebarOpen}
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </Button>
          <NavLink to="/" className="md:hidden">
            <Logo size="sm" showText variant="default" />
          </NavLink>
          <div className="flex-1" />
          {/* Tenant info - desktop only */}
          {tenant && (
            <div className="hidden md:flex items-center gap-2 mr-4 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{displayName}</span>
              <span className="text-xs">({tenant.identification})</span>
            </div>
          )}
          <ApprovalsBell />
          <ThemeToggle />
          <div className="mx-2 h-6 w-px bg-border" aria-hidden="true" />
          <HeaderUser />
        </header>

        {/* Page content - with padding for bottom nav (y el botón flotante) on mobile */}
        <main ref={mainRef} id="main-content" tabIndex={-1} className="flex-1 overflow-auto focus:outline-none">
          <div className="p-4 md:p-6 pb-24 md:pb-6 min-h-[calc(100%-80px)]">
            <Outlet />
          </div>
          {/* Footer - shows on all devices */}
          {/* En el POS la barra de cobro es más alta que el menú inferior: sin este margen tapa el final */}
          <ProductFooter
            className={
              isPos
                ? 'mb-[calc(7.5rem+env(safe-area-inset-bottom))] md:mb-0'
                : hasFab
                  ? 'mb-36 md:mb-0'
                  : 'mb-16 md:mb-0'
            }
          />
        </main>
      </div>

      {/* Bottom Navigation - mobile only */}
      <BottomNavigation />

      {/* Floating Action Button - mobile only */}
      <FloatingActionButton />
    </div>
  )
}
